import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Save, UserX, UserCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    role: '',
    status: 'active'
  });

  useEffect(() => {
    fetchUserDetail();
  }, [userId]);

  const fetchUserDetail = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data.user);
      setCompany(response.data.company);
      setFormData({
        email: response.data.user.email,
        role: response.data.user.role,
        status: response.data.user.status || 'active'
      });
    } catch (error) {
      toast.error('Failed to load user details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${userId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User updated successfully');
      fetchUserDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm('Are you sure you want to disable this user?')) return;
    
    const token = localStorage.getItem('token');
    const reason = prompt('Reason for disabling:');
    
    try {
      await axios.post(`${API}/admin/users/${userId}/disable`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User disabled successfully');
      fetchUserDetail();
    } catch (error) {
      toast.error('Failed to disable user');
    }
  };

  const handleEnable = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/admin/users/${userId}/enable`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User enabled successfully');
      fetchUserDetail();
    } catch (error) {
      toast.error('Failed to enable user');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { reason: 'Deleted from admin panel' }
      });
      toast.success('User deleted successfully');
      navigate('/admin/users');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#62D84E' }}></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-8">
        <p className="text-red-600">User not found</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <Button variant="ghost" onClick={() => navigate('/admin/users')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Users
          </Button>
          <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>User Details</h1>
          <p className="text-gray-600 mt-2">View and edit user information</p>
        </div>
        <div className="flex space-x-2">
          {user.status !== 'disabled' ? (
            <Button variant="outline" onClick={handleDisable} className="text-red-600">
              <UserX className="w-4 h-4 mr-2" />
              Disable User
            </Button>
          ) : (
            <Button variant="outline" onClick={handleEnable} className="text-green-600">
              <UserCheck className="w-4 h-4 mr-2" />
              Enable User
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowDeleteModal(true)} className="text-red-600">
            <Trash2 className="w-4 h-4 mr-2" />
            Delete User
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete User"
        message={`Are you sure you want to delete ${user.email}?`}
        entityName={user.email}
        consequences={
          user.role === 'super_admin' 
            ? ['This is a super admin account and cannot be deleted']
            : ['All user data will be marked as deleted', 'User will no longer be able to log in']
        }
        isDeleting={deleting}
      />

      {/* User Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="role">Role</Label>
              <select
                id="role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="resource_manager">Resource Manager</option>
                <option value="project_manager">Project Manager</option>
                <option value="operating_partner">Operating Partner</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>

            <div>
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>

            <Button onClick={handleSave} disabled={saving} style={{ backgroundColor: '#62D84E' }} className="w-full">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Additional Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-gray-600">User ID</Label>
              <p className="text-sm font-mono bg-gray-100 p-2 rounded">{user.id}</p>
            </div>
            
            <div>
              <Label className="text-gray-600">Company</Label>
              <p className="text-sm">{company?.name || 'No company'}</p>
            </div>

            <div>
              <Label className="text-gray-600">Company Type</Label>
              <p className="text-sm">{company?.type || 'N/A'}</p>
            </div>

            <div>
              <Label className="text-gray-600">Created At</Label>
              <p className="text-sm">{new Date(user.created_at).toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserDetailPage;
